import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { AuthService } from '../shared/auth/auth.service';
import { ObservationDefinition, Coding, CodeableConcept, Observation, Quantity, Range, Patient, Extension } from 'fhir/r4';
import { Observable, forkJoin, map, of, switchMap, tap, throwError } from 'rxjs';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { LoadingUIEnabled } from '../loading.interceptor';

export interface ObservationReferenceRange {
  type?: CodeableConcept;
  interpretation?: CodeableConcept;
  low?: Quantity;
  high?: Quantity;
  appliesTo?: CodeableConcept[];
  age?: Range;
  text?: string;
}

export interface ObservationReferenceRangeTemplate {
  id?: string;
  code?: Coding;
  display?: string;
  range: ObservationReferenceRange;
  lastUpdated?: string;
  _rawResource?: any;
}

export interface ReferenceRangeSuggestion extends ObservationReferenceRangeTemplate {
  // For normal workflow: 'low' | 'normal' | 'high' | 'unknown'
  // For typed workflow: implementation-defined token (e.g. 'critical', 'hypertension-stage-1')
  classification: string;
  workflow: 'normal' | 'typed';
}

@Injectable({ providedIn: 'root' })
export class ObservationReferenceRangeService {
  private readonly http = inject(HttpClient);
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly canonicalBase = inject(terminologyCanonicalUrlBaseToken);
  private readonly auth = inject(AuthService);
  private readonly formFieldsDataService = inject(FormFieldsSelectDataService);

  private adminGenderCodes: string[] = [];

  private readonly extensionUrl = `${this.canonicalBase}/StructureDefinition/observation-reference-range`;

  listTemplates(filter?: { code?: { system?: string; code?: string; display?: string } }): Observable<ObservationReferenceRangeTemplate[]> {
    const orgId = this.auth.getOrganizationId();
    
    // If no filter is provided, fetch latest for table view
    if (!filter?.code) {
      const params = ['_count=200', '_sort=-_lastUpdated'];
      const url = `${this.backendUrl}/ObservationDefinition?${params.join('&')}`;
      return this.http.get<any>(url, { context: new HttpContext().set(LoadingUIEnabled, false) }).pipe(
        map(bundle => this.processBundle(bundle, filter))
      );
    }

    const requests: Observable<any>[] = [];

    // 1. Fetch by exact code
    if (filter.code.code) {
      const codeParams = ['_count=200', '_sort=-_lastUpdated'];
      if (filter.code.system) {
        codeParams.push(`code=${encodeURIComponent(`${filter.code.system}|${filter.code.code}`)}`);
      } else {
        codeParams.push(`code=${encodeURIComponent(filter.code.code)}`);
      }
      const codeUrl = `${this.backendUrl}/ObservationDefinition?${codeParams.join('&')}`;
      requests.push(this.http.get<any>(codeUrl, { context: new HttpContext().set(LoadingUIEnabled, false) }));
    }

    // 2. Fetch by display text (using code:text)
    if (filter.code.display) {
      const textParams = ['_count=200', '_sort=-_lastUpdated'];
      textParams.push(`code:text=${encodeURIComponent(filter.code.display)}`);
      const textUrl = `${this.backendUrl}/ObservationDefinition?${textParams.join('&')}`;
      requests.push(this.http.get<any>(textUrl, { context: new HttpContext().set(LoadingUIEnabled, false) }));
    }

    if (requests.length === 0) {
      return of([]);
    }

    return forkJoin(requests).pipe(
      map(responses => {
        const allEntries = responses.flatMap(res => res?.entry ?? []);
        // Deduplicate by resource ID
        const uniqueEntries = Array.from(new Map(allEntries.filter((e: any) => e?.resource?.id).map((e: any) => [e.resource.id, e])).values());
        return this.processBundle({ entry: uniqueEntries }, filter);
      })
    );
  }

  private processBundle(bundle: any, filter?: { code?: { system?: string; code?: string; display?: string } }): ObservationReferenceRangeTemplate[] {
    return (bundle?.entry ?? [])
      .map((e: any) => e.resource)
      .filter((r: any) => r && r.resourceType === 'ObservationDefinition')
      .filter((r: any) => {
        try {
          if (filter?.code) {
            const coding = r.code?.coding?.[0];
            if (!coding) return false;
            
            const filterCode = filter.code.code;
            const filterDisplay = filter.code.display?.trim().toLowerCase();
            
            const codeMatch = filterCode && coding.code === filterCode;
            const displayMatch = filterDisplay && coding.display?.trim().toLowerCase() === filterDisplay;
            
            return codeMatch || displayMatch;
          }
          return true;
        } catch (e) {
          return false;
        }
      })
      .map((resource: any) => this.toTemplate(resource));
  }

  isTemplateDuplicate(a: ObservationReferenceRangeTemplate, b: ObservationReferenceRangeTemplate): boolean {
    console.log(`[Deduplication - Deep Check] Comparing templates for code: ${a.code?.code}`, { a, b });

    const lowA = a.range.low?.value;
    const lowB = b.range.low?.value;
    console.log(`  -> Comparing low value: ${lowA} === ${lowB}`);
    if (lowA !== lowB) return false;

    const highA = a.range.high?.value;
    const highB = b.range.high?.value;
    console.log(`  -> Comparing high value: ${highA} === ${highB}`);
    if (highA !== highB) return false;

    const ageALow = a.range.age?.low?.value;
    const ageBLow = b.range.age?.low?.value;
    console.log(`  -> Comparing age low: ${ageALow} === ${ageBLow}`);
    if (ageALow !== ageBLow) return false;

    const ageAHigh = a.range.age?.high?.value;
    const ageBHigh = b.range.age?.high?.value;
    console.log(`  -> Comparing age high: ${ageAHigh} === ${ageBHigh}`);
    if (ageAHigh !== ageBHigh) return false;

    const genderA = a.range.appliesTo?.[0]?.coding?.[0]?.code || 'all';
    const genderB = b.range.appliesTo?.[0]?.coding?.[0]?.code || 'all';
    console.log(`  -> Comparing gender: ${genderA} === ${genderB}`);
    if (genderA !== genderB) return false;

    const interpA = a.range.interpretation?.coding?.[0]?.code;
    const interpB = b.range.interpretation?.coding?.[0]?.code;
    console.log(`  -> Comparing interpretation: ${interpA} === ${interpB}`);
    if (interpA !== interpB) return false;

    console.log(`[Deduplication - Deep Check] EXACT MATCH FOUND!`);
    return true;
  }

  saveTemplate(template: ObservationReferenceRangeTemplate): Observable<any> {
    if (!template.code?.code || !template.code.system) {
      return throwError(() => new Error('Observation code is required.'));
    }
    const payload = this.toObservationDefinitionPayload(template);
     console.log("this is the one when all formatting is dn n  for saving", payload)
 
    if (template.id) {
      return this.http.put<any>(`${this.backendUrl}/ObservationDefinition/${template.id}`, payload);
    }
    
    return this.listTemplates({ code: template.code }).pipe(
       switchMap(existing => {
          const duplicate = existing.find(e => this.isTemplateDuplicate(e, template));
          if (duplicate) {
             console.warn('[Deduplication - Database] Skipping duplicate template for code', template.code?.code);
             return of({ resourceType: 'ObservationDefinition', id: duplicate.id, _skippedDuplicate: true });
          }
          console.log('[Deduplication - Database] Template is unique, proceeding to save to backend.', template.code?.code);
          return this.http.post<any>(`${this.backendUrl}/ObservationDefinition`, payload);
       })
    );
  }

  deleteTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.backendUrl}/ObservationDefinition/${id}`);
  }

  applyRangeToObservation(observation: Observation, range: ObservationReferenceRange & { interpretation?: any }): Observable<Observation> {
    if (!observation?.id) {
      return throwError(() => new Error('Observation id is required to apply a reference range.'));
    }
    const cleanRange = JSON.parse(JSON.stringify(range));
    const interpretationToApply = cleanRange.interpretation;
    
    // In FHIR R4, interpretation is invalid on referenceRange. We delete it, but apply it to the root observation.
    delete cleanRange.interpretation;
    // Also remove UI workflow/classification properties if they got passed
    delete cleanRange.workflow;
    delete cleanRange.classification;

    const updated: Observation = {
      ...observation,
      referenceRange: [cleanRange]
    };
    
    if (interpretationToApply) {
      updated.interpretation = Array.isArray(interpretationToApply) ? interpretationToApply : [interpretationToApply];
    } else {
      delete updated.interpretation; // clear interpretation if new range has none
    }

    return this.http.put<Observation>(`${this.backendUrl}/Observation/${observation.id}`, updated);
  }

  updateObservation(observation: Observation): Observable<Observation> {
    if (!observation?.id) {
      return throwError(() => new Error('Observation id is required for update.'));
    }
    return this.http.put<Observation>(`${this.backendUrl}/Observation/${observation.id}`, observation);
  }

  matchRangesForObservation(observation: Observation, patient?: Patient): Observable<ReferenceRangeSuggestion[]> {
    const coding = observation?.code?.coding?.[0];
    if (!coding) {
      console.warn('[RefRange Matcher] No code found on observation, returning []');
      return of([]);
    }
    console.log('[RefRange Matcher] Fetching templates for code:', coding);
    return this.listTemplates({ code: coding }).pipe(
      tap((templates: ObservationReferenceRangeTemplate[]) => console.log('[RefRange Matcher] Raw templates fetched from backend:', templates)),
      map((templates: ObservationReferenceRangeTemplate[]) => this.evaluateTemplatesForObservation(observation, templates, patient))
    );
  }

  evaluateTemplatesForObservation(observation: Observation, templates: ObservationReferenceRangeTemplate[], patient?: Patient): ReferenceRangeSuggestion[] {
    console.log('[RefRange Matcher] Evaluating templates. Patient context:', patient, 'Templates:', templates);
    
    // 1. Filter out duplicates from the backend to ensure a clean UI
    const uniqueTemplates: ObservationReferenceRangeTemplate[] = [];
    for (const t of templates) {
      if (!uniqueTemplates.some(u => this.isTemplateDuplicate(u, t))) {
        uniqueTemplates.push(t);
      }
    }

    const filteredTemplates = patient ? uniqueTemplates.filter(t => {
      const match = this.matchesPatientContext(t.range, patient);
      if (!match) console.log('[RefRange Matcher] Template', t.id, 'failed patient context match (age/gender)');
      return match;
    }) : uniqueTemplates;
    
    console.log('[RefRange Matcher] Templates after patient context filter:', filteredTemplates);
    const typed = this.classifyTypedRanges(observation, filteredTemplates);
    const normal = this.classifyNormalRanges(observation, filteredTemplates);
    console.log('[RefRange Matcher] Typed matches:', typed, 'Normal matches:', normal);
    return [...typed, ...normal];
  }

  private unitsMatch(obsValue?: any, templateBound?: any): boolean {
    if (!obsValue?.unit && !obsValue?.code && !templateBound?.unit && !templateBound?.code) {
      return true;
    }

    if (obsValue?.code && templateBound?.code && obsValue?.system === templateBound?.system) {
      if (obsValue.code === templateBound.code) return true;
    }

    const obsUnitText = (obsValue?.unit || obsValue?.code || '').trim().toLowerCase();
    const tempUnitText = (templateBound?.unit || templateBound?.code || '').trim().toLowerCase();
    
    if (obsUnitText && tempUnitText) {
      const match = obsUnitText === tempUnitText;
      if (!match) console.log(`[RefRange Matcher] Unit mismatch: obsUnitText='${obsUnitText}' vs tempUnitText='${tempUnitText}'`);
      return match;
    }

    console.log('[RefRange Matcher] Unit mismatch (fallback false). obsValue:', obsValue, 'templateBound:', templateBound);
    return false;
  }

  // Typed-range classification: return templates whose interval contains the observation value,
  // using range.type as the returned classification token.
  private classifyTypedRanges(observation: Observation, templates: ObservationReferenceRangeTemplate[]): ReferenceRangeSuggestion[] {
    const value = Number(observation?.valueQuantity?.value);
    if (Number.isNaN(value)) return [];
    const matches: ReferenceRangeSuggestion[] = [];
    for (const t of templates) {
      const r = t.range;
      if (!r) continue;
      
      const boundWithUnit = r.low || r.high;
      if (boundWithUnit && !this.unitsMatch(observation?.valueQuantity, boundWithUnit)) {
        continue;
      }

      const lowN = r.low?.value != null ? Number(r.low.value) : null;
      const highN = r.high?.value != null ? Number(r.high.value) : null;
      const contains = (lowN == null || value >= lowN) && (highN == null || value <= highN);
      if (contains) {
        const typeText = this.rangeTypeLabel(r) || 'typed';
        matches.push({ ...t, classification: typeText, workflow: 'typed' });
      }
    }
    return matches;
  }

  // Normal-range interpretation: consider ranges whose type is 'normal' or missing
  // and derive low/normal/high (inclusive boundaries)
  private classifyNormalRanges(observation: Observation, templates: ObservationReferenceRangeTemplate[]): ReferenceRangeSuggestion[] {
    const value = Number(observation?.valueQuantity?.value);
    if (Number.isNaN(value)) {
      console.log('[RefRange Matcher] classifyNormalRanges: value is NaN');
      return [];
    }
    const results: ReferenceRangeSuggestion[] = [];
    for (const t of templates) {
      const r = t.range;
      if (!r) continue;
      const typeLabel = this.rangeTypeLabel(r);
      if (typeLabel && typeLabel.toLowerCase() !== 'normal') continue; // not a normal range
      
      const boundWithUnit = r.low || r.high;
      if (boundWithUnit && !this.unitsMatch(observation?.valueQuantity, boundWithUnit)) {
        console.log('[RefRange Matcher] classifyNormalRanges: unitsMatch failed for template', t.id);
        continue;
      }

      const lowN = r.low?.value != null ? Number(r.low.value) : null;
      const highN = r.high?.value != null ? Number(r.high.value) : null;
      let classification = 'unknown';
      if (lowN != null && value < lowN) classification = 'low';
      else if ((lowN == null && highN == null)) classification = 'unknown';
      else if ((lowN == null || value >= lowN) && (highN == null || value <= highN)) classification = 'normal';
      else if (highN != null && value > highN) classification = 'high';
      results.push({ ...t, classification, workflow: 'normal' });
    }
    return results;
  }

  // Helper to derive a textual label for range.type (prefer code, then display, then text)
  private rangeTypeLabel(range?: ObservationReferenceRange): string | null {
    if (!range?.type) return null;
    const c = range.type.coding?.[0];
    if (c?.code) return String(c.code);
    if (c?.display) return String(c.display);
    if (range.type.text) return String(range.type.text);
    return null;
  }

  private toTemplate(resource: any): ObservationReferenceRangeTemplate {
    const code = resource?.code?.coding?.[0];
    const range = this.parseObservationDefinition(resource);
    return {
      id: resource.id,
      code,
      display: resource.code?.text || code?.display || code?.code,
      range,
      lastUpdated: resource.meta?.lastUpdated,
      _rawResource: resource
    };
  }

  private toObservationDefinitionPayload(template: ObservationReferenceRangeTemplate): any {
    const coding: Coding[] = template.code ? [{ ...template.code }] : [];
    const payload = template._rawResource ? { ...template._rawResource } : { resourceType: 'ObservationDefinition' };
    
    payload.id = template.id;
    payload.code = coding.length
      ? {
        coding,
        text: template.display || coding[0].display || coding[0].code
      }
      : undefined;
      
    const interval = this.buildQualifiedInterval(template.range);
    if (interval) {
      payload.qualifiedInterval = interval;
    } else {
      delete payload.qualifiedInterval;
    }
    
    return payload;
  }

  private buildQualifiedInterval(range?: ObservationReferenceRange): any[] | undefined {
    if (!range) return undefined;
    const interval: any = {};
    if (range.type) interval.context = range.type;
    if (range.low || range.high) {
      interval.range = {};
      if (range.low) interval.range.low = range.low;
      if (range.high) interval.range.high = range.high;
    }
    if (range.appliesTo?.length) interval.appliesTo = range.appliesTo;
    if (range.age) interval.age = range.age;
    
    if (range.interpretation) {
      interval.extension = interval.extension || [];
      interval.extension.push({
        url: 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation',
        valueCodeableConcept: range.interpretation
      });
    }
    
    // Check if interval is empty
    if (Object.keys(interval).length === 0) return undefined;
    
    return [interval];
  }

  private parseObservationDefinition(resource: any): ObservationReferenceRange {
    const range: ObservationReferenceRange = {};
    const interval = resource.qualifiedInterval?.[0];
    if (!interval) return range;
    
    if (interval.context) range.type = interval.context;
    if (interval.range?.low) range.low = interval.range.low;
    if (interval.range?.high) range.high = interval.range.high;
    if (interval.appliesTo) range.appliesTo = interval.appliesTo;
    if (interval.age) range.age = interval.age;
    
    const interpretationExt = interval.extension?.find((e: any) => e.url === 'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation');
    if (interpretationExt?.valueCodeableConcept) {
      range.interpretation = interpretationExt.valueCodeableConcept;
    }
    
    return range;
  }

  private matchesPatientContext(range: ObservationReferenceRange, patient?: Patient): boolean {
    if (!patient) {
      return true;
    }
    const gender = (patient.gender ?? '').toLowerCase();
    console.log(range, gender);
// $#$    /* debugger; */
    if (range.appliesTo?.length) {
      const matchesGender = range.appliesTo.some(apply => this.matchesGender(apply, gender));
      if (!matchesGender) {
        return false;
      }
    }
    if (range.age?.low || range.age?.high) {
      const ageYears = this.getPatientAgeInYears(patient);
      if (ageYears == null) {
        return true;
      }
      console.log(range, ageYears);
      const within = this.isAgeWithinRange(ageYears, range.age);
      if (!within) {
        return false;
      }
    }
    return true;
  }

  private matchesGender(apply: CodeableConcept | undefined, gender: string): boolean {
    if (!apply || !gender) {
      return true;
    }
    // Ensure we have the administrative-gender codes loaded (lazy)
    this.loadAdminGenderCodesIfNeeded();

    const codes = apply.coding ?? [];
    const target = (gender || '').toString().toLowerCase();

    // Prefer code matching only (case-insensitive). If the patient.gender value is
    // not present in the known administrative-gender codes, emit a debug warning
    // so callers know the assumption about patient.gender values may be violated.
    if (this.adminGenderCodes.length && !this.adminGenderCodes.map(c => c.toLowerCase()).includes(target)) {
      console.debug('[ObservationReferenceRangeService] patient.gender value not found in administrative-gender ValueSet codes:', gender);
    }

    return codes.some(c => ((c.code ?? '') as string).toLowerCase() === target);
  }

  private loadAdminGenderCodesIfNeeded(): void {
    if (this.adminGenderCodes.length) return;
    try {
      const url = this.formFieldsDataService.allUrls?.patient?.gender;
      if (!url) return;
      this.http.get(url, { context: new HttpContext().set(LoadingUIEnabled, false) }).subscribe({
        next: (val: any) => {
          try {
            const codes = this.formFieldsDataService.baseFunctionToRetrieveValueCodes(val);
            if (Array.isArray(codes) && codes.length) {
              this.adminGenderCodes = codes.map(c => (c || '').toString());
            }
          } catch (e) {
            // non-blocking
          }
        },
        error: () => { /* non-blocking */ }
      });
    } catch (e) {
      // non-blocking
    }
  }

  private getPatientAgeInYears(patient: Patient): number | null {
    if (!patient?.birthDate) {
      return null;
    }
    const birth = new Date(patient.birthDate);
    if (Number.isNaN(birth.getTime())) {
      return null;
    }
    const diffMs = Date.now() - birth.getTime();
    return diffMs / (1000 * 60 * 60 * 24 * 365.25);
  }

  private isAgeWithinRange(ageYears: number, range?: Range): boolean {
    if (!range) {
      return true;
    }
    if (range.low?.value != null) {
      const minYears = this.convertToYears(range.low);
      if (minYears != null && ageYears < minYears) {
        return false;
      }
    }
    if (range.high?.value != null) {
      const maxYears = this.convertToYears(range.high);
      if (maxYears != null && ageYears > maxYears) {
        return false;
      }
    }
    return true;
  }

  public convertToYears(quantity: Quantity): number | null {
    if (!quantity?.value) {
      return null;
    }
    const unit = (quantity.code || quantity.unit || '').toLowerCase();
    const value = Number(quantity.value);
    if (Number.isNaN(value)) {
      return null;
    }
    switch (unit) {
      case 'a':
      case 'yr':
      case 'year':
      case 'years':
        return value;
      case 'mo':
      case 'month':
      case 'months':
        return value / 12;
      case 'wk':
      case 'week':
      case 'weeks':
        return value / 52;
      case 'd':
      case 'day':
      case 'days':
        return value / 365;
      default:
        return value;
    }
  }

  describeRange(range?: ObservationReferenceRange): string {
    if (!range) {
      return '-';
    }
    const parts: string[] = [];
    if (range.low?.value != null) {
      const lowUnit = range.low.unit || range.low.code || '';
      parts.push(`Low: ${range.low.value} ${lowUnit}`.trim());
    }
    if (range.high?.value != null) {
      const highUnit = range.high.unit || range.high.code || '';
      parts.push(`High: ${range.high.value} ${highUnit}`.trim());
    }
    if (!parts.length && range.text) {
      parts.push(range.text);
    }
    return parts.length ? parts.join(' | ') : 'Configured';
  }

  describeAppliesTo(range?: ObservationReferenceRange): string {
    if (!range?.appliesTo?.length) {
      return 'All patients';
    }
    return range.appliesTo
      .map(concept => concept?.text || concept?.coding?.[0]?.display || concept?.coding?.[0]?.code || 'Unknown')
      .join(', ');
  }

  private formatUcumTimeUnit(unitCode: string): string {
    const map: Record<string, string> = {
      'a': 'years',
      'yr': 'years',
      'year': 'years',
      'mo': 'months',
      'month': 'months',
      'wk': 'weeks',
      'week': 'weeks',
      'd': 'days',
      'day': 'days',
      'h': 'hours',
      'min': 'minutes'
    };
    return map[unitCode?.toLowerCase()] || unitCode || '';
  }

  describeAge(range?: ObservationReferenceRange): string {
    const age = range?.age;
    if (!age || (!age.low && !age.high)) {
      return 'All ages';
    }
    const segments: string[] = [];
    if (age.low?.value != null) {
      const rawUnit = age.low.unit || age.low.code || '';
      const formattedUnit = this.formatUcumTimeUnit(rawUnit);
      segments.push(`>= ${age.low.value} ${formattedUnit}`.trim());
    }
    if (age.high?.value != null) {
      const rawUnit = age.high.unit || age.high.code || '';
      const formattedUnit = this.formatUcumTimeUnit(rawUnit);
      segments.push(`<= ${age.high.value} ${formattedUnit}`.trim());
    }
    return segments.join(' and ');
  }

  private classifyValue(observation: Observation, range: ObservationReferenceRange): 'low' | 'high' | 'within' | 'unknown' {
    const valueQuantity = observation?.valueQuantity;
    if (!valueQuantity?.value) {
      return 'unknown';
    }
    const value = Number(valueQuantity.value);
    if (Number.isNaN(value)) {
      return 'unknown';
    }
    if (range.low?.value != null) {
      const low = Number(range.low.value);
      if (!Number.isNaN(low) && value < low) {
        return 'low';
      }
    }
    if (range.high?.value != null) {
      const high = Number(range.high.value);
      if (!Number.isNaN(high) && value > high) {
        return 'high';
      }
    }
    if (range.low?.value != null || range.high?.value != null) {
      return 'within';
    }
    return 'unknown';
  }
}
